// RegenPreview — unified regen confirm UI replacing RegenConfirmPanel.
//
// 19BJ Session 2: Regen embedded architecture migration.
// Design doc: docs/architecture/regen-embedded-design.md §2.2.
//
// Inline overlay/popover for embedded regen (single-item + chapter-scoped),
// positioned near the trigger button via a portal.
//
// Session 5: the full-mode placeholder has been removed — it was dead code.
// RegenConfirmPanel is retained for Regen Center batch confirm (R6 deviation,
// approved Session 3), so RegenPreview is inline-only.
//
// R6: RegenConfirmPanel retained for Regen Center batch confirm.
// R7: auto-apply default false (safety first).

import { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Check, X, ChevronDown, ChevronRight } from 'lucide-react';
import { MarkdownBlock } from './markdown-block.js';
import { useT } from '../i18n/index.js';
import { extractRegenPreview } from '../lib/regen-utils.js';
import type { ItemPreview } from '../lib/regen-utils.js';
import type { RegenAction } from '@examforge/shared/schemas';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface RegenPreviewProps {
  /** Items proposed by the regen. */
  newItems: unknown[];
  /** Snapshot of original items (for diff display). */
  originalItems?: unknown[];
  /** Stage being regenerated. */
  stage: string;
  /** The RegenAction that produced these items. */
  action: RegenAction | null;
  /** Which path was used (quick vs deep) — for badge display. */
  path: 'quick' | 'deep' | null;
  /** Token cost info (Quick path returns it synchronously). */
  cost: { tokensIn: number; tokensOut: number; costRmbEstimate: number } | null;
  /** Ref to the trigger button for positioning the inline overlay. */
  triggerRef?: React.RefObject<HTMLElement | null>;
  /** Called when user accepts (with optionally filtered items). */
  onAccept: (selectedItems: unknown[]) => void;
  /** Called when user rejects all items. */
  onReject: () => void;
}

// ── Inline overlay positioning ────────────────────────────────────────────────

interface OverlayStyle {
  top: number;
  left: number;
}

function useOverlayPosition(
  triggerRef: React.RefObject<HTMLElement | null>,
  open: boolean,
): OverlayStyle | null {
  const [pos, setPos] = useState<OverlayStyle | null>(null);

  useEffect(() => {
    if (!open || !triggerRef?.current) {
      setPos(null);
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    // Position below the trigger, aligned to the right edge.
    setPos({
      top: rect.bottom + window.scrollY + 4,
      left: rect.right + window.scrollX - 320, // 320px wide overlay
    });

    const onResize = () => {
      if (triggerRef.current) {
        const r = triggerRef.current.getBoundingClientRect();
        setPos({
          top: r.bottom + window.scrollY + 4,
          left: r.right + window.scrollX - 320,
        });
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open, triggerRef]);

  return pos;
}

// ── Component ──────────────────────────────────────────────────────────────────

export function RegenPreview({
  newItems,
  stage,
  action,
  path,
  cost,
  triggerRef,
  onAccept,
  onReject,
}: RegenPreviewProps) {
  const t = useT();
  const [selected, setSelected] = useState<Set<string>>(() => {
    // Default: all selected.
    const previews = newItems
      .map((it) => extractRegenPreview(it, stage as Parameters<typeof extractRegenPreview>[1]))
      .filter((p): p is ItemPreview => p !== null);
    return new Set(previews.map((p) => p.id));
  });
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  // Parse previews.
  const previews = newItems
    .map((it) => extractRegenPreview(it, stage as Parameters<typeof extractRegenPreview>[1]))
    .filter((p): p is ItemPreview => p !== null);

  const overlayPos = useOverlayPosition(triggerRef as React.RefObject<HTMLElement | null>, true);

  const toggleItem = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleExpand = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const handleAccept = useCallback(() => {
    const filtered = newItems.filter((it) => {
      if (!it || typeof it !== 'object') return false;
      return selected.has((it as Record<string, unknown>)['id'] as string);
    });
    onAccept(filtered);
  }, [newItems, selected, onAccept]);

  const noneSelected = selected.size === 0;
  const isReplace = action?.kind === 'replace';
  const isDelete = action?.kind === 'delete';

  // ── Inline overlay render ─────────────────────────────────────────────────

  if (overlayPos) {
    return createPortal(
      <div
        className="regen-preview regen-preview--inline"
        style={{ top: overlayPos.top, left: Math.max(8, overlayPos.left) }}
      >
        {/* Header */}
        <div className="regen-preview-header">
          <span className="regen-preview-title">
            {isDelete ? t('regenPreview.confirmDelete') : isReplace ? t('regenPreview.confirmReplace') : t('regenPreview.confirmAdd')}
          </span>
          {path === 'quick' && <span className="regen-preview-badge regen-preview-badge--quick">{t('regenPreview.badgeQuick')}</span>}
          {path === 'deep' && <span className="regen-preview-badge regen-preview-badge--deep">{t('regenPreview.badgeDeep')}</span>}
          <button
            type="button"
            className="regen-preview-close"
            onClick={onReject}
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>

        {/* Items */}
        <ol className="regen-preview-list">
          {previews.map((pv) => {
            const isSelected = selected.has(pv.id);
            const isExpanded = expanded.has(pv.id);
            return (
              <li
                key={pv.id}
                className={`regen-preview-item${isSelected ? ' is-selected' : ''}`}
              >
                <label className="regen-preview-item-row">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleItem(pv.id)}
                    className="regen-preview-checkbox"
                  />
                  <span className="regen-preview-item-kind">{pv.kindLabel}</span>
                  <span className="regen-preview-item-primary">
                    {pv.primary.length > 80 ? `${pv.primary.slice(0, 80)}…` : pv.primary}
                  </span>
                  <button
                    type="button"
                    className="regen-preview-expand-btn"
                    onClick={(e) => { e.preventDefault(); toggleExpand(pv.id); }}
                    aria-label={isExpanded ? 'Collapse' : 'Expand'}
                  >
                    {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  </button>
                </label>
                {isExpanded && (
                  <div className="regen-preview-item-detail">
                    <MarkdownBlock>{pv.expandedContent}</MarkdownBlock>
                  </div>
                )}
              </li>
            );
          })}
        </ol>

        {/* Cost info */}
        {cost && (
          <div className="regen-preview-cost">
            <span>{cost.tokensIn}↑ {cost.tokensOut}↓ tokens</span>
            <span>≈¥{cost.costRmbEstimate.toFixed(3)}</span>
          </div>
        )}

        {/* Actions */}
        <div className="regen-preview-actions">
          <button
            type="button"
            className="regen-preview-accept-btn"
            onClick={handleAccept}
            disabled={noneSelected}
          >
            <Check size={14} />
            <span>{t('regenPreview.accept')}{selected.size > 0 ? ` (${selected.size})` : ''}</span>
          </button>
          <button
            type="button"
            className="regen-preview-reject-btn"
            onClick={onReject}
          >
            <X size={14} />
            <span>{t('regenPreview.discard')}</span>
          </button>
        </div>
      </div>,
      document.body,
    );
  }

  return null;
}